/**
 * Cart Drawer (Sidecart) JavaScript
 * Source: Figma 4360:9288, 4747:1373, 4747:2427
 * Handles cart API, gift selection, cross-sell, and interactions
 */

(function() {
  'use strict';

  const CartDrawer = {
    drawer: null,
    overlay: null,
    itemsContainer: null,
    emptyContainer: null,
    footer: null,
    countEl: null,
    subtotalEl: null,
    checkoutBtn: null,
    crossSellSwiper: null,
    giftDeclined: false,
    giftDeclinedTotal: 0,
    lastKnownTotal: 0,
    isLoading: false,

    selectors: {
      drawer: '[data-cart-drawer]',
      overlay: '[data-cart-drawer-overlay]',
      closeBtn: '[data-cart-drawer-close]',
      itemsContainer: '[data-cart-items]',
      emptyContainer: '[data-cart-empty]',
      itemsWrapper: '[data-cart-items-container]',
      footer: '[data-cart-footer]',
      countEl: '[data-cart-count-display]',
      bundlePromo: '[data-bundle-promo]',
      subtotalEl: '[data-cart-subtotal]',
      checkoutBtn: '[data-cart-checkout]',
      progressMessage: '[data-progress-message]',
      progressFill: '[data-progress-fill]',
      progressDotGift: '[data-dot-gift]',
      progressDotShipping: '[data-dot-shipping]',
      giftSelection: '[data-gift-selection]',
      addGift: '[data-add-gift]',
      declineGift: '[data-decline-gift]',
      crossSellSwiper: '[data-cross-sell-swiper]',
      crossSellDots: '[data-cross-sell-dots]',
      crossSellSection: '[data-cart-cross-sell]',
      cartItem: '[data-line-key]',
      removeItem: '[data-remove-item]',
      quantityMinus: '[data-quantity-minus]',
      quantityPlus: '[data-quantity-plus]',
      quantityValue: '[data-quantity-value]',
      upsellToggle: '[data-toggle-upsell]',
      upsellContainer: '[data-upsell-toggle]',
      addToCart: '[data-add-to-cart]',
      variantId: '[data-variant-id]'
    },

    init() {
      this.drawer = document.querySelector(this.selectors.drawer);
      if (!this.drawer) return;

      this.cacheElements();
      this.bindEvents();
      this.initSwiper();
    },

    cacheElements() {
      this.overlay = this.drawer.querySelector(this.selectors.overlay);
      this.itemsContainer = this.drawer.querySelector(this.selectors.itemsContainer);
      this.emptyContainer = this.drawer.querySelector(this.selectors.emptyContainer);
      this.itemsWrapper = this.drawer.querySelector(this.selectors.itemsWrapper);
      this.footer = this.drawer.querySelector(this.selectors.footer);
      this.countEl = this.drawer.querySelector(this.selectors.countEl);
      this.subtotalEl = this.drawer.querySelector(this.selectors.subtotalEl);
      this.checkoutBtn = this.drawer.querySelector(this.selectors.checkoutBtn);
    },

    bindEvents() {
      // Open drawer from header cart icon
      document.querySelectorAll('[data-cart-drawer-open]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        });
      });

      // Delegated add-to-cart - skip if another handler already processed
      document.addEventListener('click', (e) => {
        const addBtn = e.target.closest(this.selectors.addToCart);
        if (addBtn && !e.defaultPrevented) {
          e.preventDefault();
          const variantId = addBtn.dataset.variantId;
          if (variantId) {
            const section = addBtn.closest('[data-section-type]');
            const quantitySelect = section?.querySelector('[data-quantity-select]');
            const quantity = quantitySelect ? parseInt(quantitySelect.value, 10) : 1;
            this.addItem(variantId, quantity);
          }
        }
      });

      // Close drawer events
      this.overlay?.addEventListener('click', () => this.close());

      document.querySelectorAll(this.selectors.closeBtn).forEach(btn => {
        btn.addEventListener('click', () => this.close());
      });

      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.close();
        }
      });

      // Cart item interactions (delegated)
      this.drawer.addEventListener('click', (e) => {
        const target = e.target;

        if (target.closest(this.selectors.removeItem)) {
          const item = target.closest(this.selectors.cartItem);
          if (item) this.removeItem(item.dataset.lineKey);
        }

        if (target.closest(this.selectors.quantityMinus)) {
          const item = target.closest(this.selectors.cartItem);
          if (item) {
            const valueEl = item.querySelector(this.selectors.quantityValue);
            const currentQty = parseInt(valueEl?.textContent || '1', 10);
            if (currentQty > 1) {
              this.updateItem(item.dataset.lineKey, currentQty - 1);
            } else {
              this.removeItem(item.dataset.lineKey);
            }
          }
        }

        if (target.closest(this.selectors.quantityPlus)) {
          const item = target.closest(this.selectors.cartItem);
          if (item) {
            const valueEl = item.querySelector(this.selectors.quantityValue);
            const currentQty = parseInt(valueEl?.textContent || '1', 10);
            this.updateItem(item.dataset.lineKey, currentQty + 1);
          }
        }

        if (target.closest(this.selectors.upsellToggle)) {
          const upsellContainer = target.closest(this.selectors.upsellContainer);
          if (upsellContainer) this.toggleUpsell(upsellContainer);
        }

        if (target.closest(this.selectors.addGift)) {
          e.preventDefault();
          const btn = target.closest(this.selectors.addGift);
          const variantId = btn.dataset.variantId;
          if (variantId) this.addGift(variantId);
        }

        if (target.closest(this.selectors.declineGift)) {
          e.preventDefault();
          this.declineGift();
        }
      });

      // Custom cart events
      document.addEventListener('cart:open', () => this.open());
      document.addEventListener('cart:close', () => this.close());
      document.addEventListener('cart:refresh', () => this.refreshCart());
      document.addEventListener('cart:updated', () => {
        this.refreshCart();
        this.open();
      });
    },

    initSwiper() {
      const swiperEl = this.drawer.querySelector(this.selectors.crossSellSwiper);
      if (!swiperEl || typeof Swiper === 'undefined') return;

      this.crossSellSwiper = new Swiper(swiperEl, {
        slidesPerView: 'auto',
        spaceBetween: 6,
        on: {
          slideChange: () => this.updateDots()
        }
      });
      this.buildDots();
      this.bindDotClicks();
      this.updateDots();
    },

    buildDots() {
      const dotsEl = this.drawer.querySelector(this.selectors.crossSellDots);
      if (!dotsEl || !this.crossSellSwiper) return;

      const totalSlides = this.crossSellSwiper.slides.length;
      let html = '';
      for (let i = 0; i < totalSlides; i++) {
        html += `<button type="button" class="cart-drawer__cross-sell-dot${i === 0 ? ' cart-drawer__cross-sell-dot--active' : ''}" data-dot-index="${i}" aria-label="Slide ${i + 1}"></button>`;
      }
      dotsEl.innerHTML = html;
    },

    bindDotClicks() {
      const dotsEl = this.drawer.querySelector(this.selectors.crossSellDots);
      if (!dotsEl) return;

      dotsEl.addEventListener('click', (e) => {
        const dot = e.target.closest('[data-dot-index]');
        if (!dot || !this.crossSellSwiper) return;
        this.crossSellSwiper.slideTo(parseInt(dot.dataset.dotIndex, 10));
      });
    },

    updateDots() {
      const dotsEl = this.drawer.querySelector(this.selectors.crossSellDots);
      if (!dotsEl || !this.crossSellSwiper) return;

      const dots = dotsEl.querySelectorAll('.cart-drawer__cross-sell-dot');
      const activeIndex = this.crossSellSwiper.activeIndex;

      dots.forEach((dot, index) => {
        dot.classList.toggle('cart-drawer__cross-sell-dot--active', index === activeIndex);
      });
    },

    destroySwiper() {
      if (this.crossSellSwiper) {
        this.crossSellSwiper.destroy(true, true);
        this.crossSellSwiper = null;
      }
    },

    open() {
      if (!this.drawer) return;
      this.drawer.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';

      const focusableElements = this.drawer.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );
      if (focusableElements.length) focusableElements[0].focus();
    },

    close() {
      if (!this.drawer) return;
      this.drawer.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    },

    isOpen() {
      return this.drawer?.getAttribute('aria-hidden') === 'false';
    },

    setLoading(loading) {
      this.isLoading = loading;
      this.drawer.classList.toggle('cart-drawer--loading', loading);
    },

    // ========================================
    // CART API OPERATIONS
    // ========================================

    async addItem(variantId, quantity = 1) {
      if (this.isLoading) return;
      this.setLoading(true);

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: quantity })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to add item');
        }

        await this.refreshCart();
        this.open();
      } catch (error) {
        console.error('Cart add error:', error);
      } finally {
        this.setLoading(false);
      }
    },

    async addGift(variantId) {
      if (this.isLoading) return;
      this.setLoading(true);

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: variantId, quantity: 1, properties: { '_gift': 'true' } })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Failed to add gift');
        }

        // Immediately hide gift selection
        const giftSection = this.drawer.querySelector(this.selectors.giftSelection);
        if (giftSection) {
          giftSection.classList.add('hidden');
          giftSection.style.display = '';
        }

        await this.refreshCart();
      } catch (error) {
        console.error('Gift add error:', error);
      } finally {
        this.setLoading(false);
      }
    },

    declineGift() {
      this.giftDeclined = true;
      this.giftDeclinedTotal = this.lastKnownTotal || 0;
      const giftSection = this.drawer.querySelector(this.selectors.giftSelection);
      if (giftSection) {
        giftSection.classList.add('hidden');
        giftSection.style.display = '';
      }
    },

    async updateItem(lineKey, quantity) {
      if (this.isLoading) return;
      this.setLoading(true);

      try {
        const response = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity: quantity })
        });

        if (!response.ok) throw new Error('Failed to update item');

        await this.refreshCart();
      } catch (error) {
        console.error('Cart update error:', error);
      } finally {
        this.setLoading(false);
      }
    },

    async removeItem(lineKey) {
      if (this.isLoading) return;

      const item = this.drawer.querySelector(`[data-line-key="${lineKey}"]`);
      if (item) item.classList.add('cart-item--removing');

      await new Promise(resolve => setTimeout(resolve, 300));
      await this.updateItem(lineKey, 0);
    },

    async toggleUpsell(upsellContainer) {
      const complementaryVariantId = upsellContainer.dataset.complementaryVariantId;
      const toggleBtn = upsellContainer.querySelector(this.selectors.upsellToggle);
      const isActive = toggleBtn?.getAttribute('aria-pressed') === 'true';

      if (!complementaryVariantId) return;
      this.setLoading(true);

      try {
        if (isActive) {
          const cartResponse = await fetch('/cart.js');
          const cart = await cartResponse.json();
          const complementaryItem = cart.items.find(
            item => item.variant_id === parseInt(complementaryVariantId, 10)
          );

          if (complementaryItem) {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: complementaryItem.key, quantity: 0 })
            });
          }
        } else {
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({
              id: parseInt(complementaryVariantId, 10),
              quantity: 1,
              properties: { '_upsell': 'true' }
            })
          });
        }

        await this.refreshCart();
      } catch (error) {
        console.error('Upsell toggle error:', error);
      } finally {
        this.setLoading(false);
      }
    },

    // ========================================
    // CART REFRESH (Section Rendering + Fallback)
    // ========================================

    async refreshCart(skipGiftCheck = false) {
      try {
        const response = await fetch('/cart?sections=cart-drawer', {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (!response.ok) throw new Error('Section render ' + response.status);

        const json = await response.json();
        const html = json['cart-drawer'];

        if (!html) throw new Error('Empty section response');

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const newDrawer = doc.querySelector(this.selectors.drawer);

        if (newDrawer) {
          this.applySectionUpdate(newDrawer);
        }

        if (!skipGiftCheck) {
          const giftRemoved = await this.removeGiftIfIneligible();
          if (giftRemoved) {
            await this.refreshCart(true);
            return;
          }
        }

        this.updateBundlePrices();
        document.dispatchEvent(new CustomEvent('cart:drawer-refreshed'));
      } catch (error) {
        await this.refreshCartFallback(skipGiftCheck);
      }

      // ALWAYS verify gift selection via /cart.js as final safety net
      await this.verifyGiftSelection();
    },

    applySectionUpdate(newDrawer) {
      // Items
      const newItems = newDrawer.querySelector(this.selectors.itemsContainer);
      if (this.itemsContainer && newItems) {
        this.itemsContainer.innerHTML = newItems.innerHTML;
      }

      // Empty state
      const newEmpty = newDrawer.querySelector(this.selectors.emptyContainer);
      const newItemsWrapper = newDrawer.querySelector(this.selectors.itemsWrapper);
      const currentEmpty = this.drawer.querySelector(this.selectors.emptyContainer);
      const currentItemsWrapper = this.drawer.querySelector(this.selectors.itemsWrapper);
      if (currentEmpty && newEmpty) currentEmpty.className = newEmpty.className;
      if (currentItemsWrapper && newItemsWrapper) currentItemsWrapper.className = newItemsWrapper.className;

      // Count
      const newCount = newDrawer.querySelector(this.selectors.countEl);
      document.querySelectorAll(this.selectors.countEl).forEach(el => {
        if (newCount) el.textContent = newCount.textContent;
      });

      // Progress
      const newProgressMessage = newDrawer.querySelector(this.selectors.progressMessage);
      const currentProgressMessage = this.drawer.querySelector(this.selectors.progressMessage);
      if (currentProgressMessage && newProgressMessage) {
        currentProgressMessage.innerHTML = newProgressMessage.innerHTML;
      }

      const newProgressFill = newDrawer.querySelector(this.selectors.progressFill);
      const currentProgressFill = this.drawer.querySelector(this.selectors.progressFill);
      if (currentProgressFill && newProgressFill) {
        currentProgressFill.style.width = newProgressFill.style.width;
      }

      // Progress dots
      const newDotGift = newDrawer.querySelector(this.selectors.progressDotGift);
      const currentDotGift = this.drawer.querySelector(this.selectors.progressDotGift);
      if (currentDotGift && newDotGift) currentDotGift.className = newDotGift.className;

      const newDotShipping = newDrawer.querySelector(this.selectors.progressDotShipping);
      const currentDotShipping = this.drawer.querySelector(this.selectors.progressDotShipping);
      if (currentDotShipping && newDotShipping) currentDotShipping.className = newDotShipping.className;

      // Gift selection HTML (content update only - visibility handled by verifyGiftSelection)
      const newGiftSelection = newDrawer.querySelector(this.selectors.giftSelection);
      const currentGiftSelection = this.drawer.querySelector(this.selectors.giftSelection);
      if (newGiftSelection && currentGiftSelection) {
        currentGiftSelection.outerHTML = newGiftSelection.outerHTML;
      } else if (newGiftSelection && !currentGiftSelection) {
        const itemsContainer = this.drawer.querySelector(this.selectors.itemsWrapper);
        if (itemsContainer) itemsContainer.insertAdjacentHTML('beforeend', newGiftSelection.outerHTML);
      } else if (!newGiftSelection && currentGiftSelection) {
        currentGiftSelection.remove();
      }

      // Data attributes
      if (newDrawer.dataset.giftEligible) {
        this.drawer.dataset.giftEligible = newDrawer.dataset.giftEligible;
      }

      // Footer
      const newFooter = newDrawer.querySelector(this.selectors.footer);
      if (this.footer && newFooter) this.footer.className = newFooter.className;

      // CTA subtotal
      const newCtaSubtotal = newDrawer.querySelector('[data-cart-subtotal-cta]');
      const currentCtaSubtotal = this.drawer.querySelector('[data-cart-subtotal-cta]');
      if (currentCtaSubtotal && newCtaSubtotal) currentCtaSubtotal.textContent = newCtaSubtotal.textContent;

      // Cross-sell
      const newCrossSell = newDrawer.querySelector(this.selectors.crossSellSection);
      const currentCrossSell = this.drawer.querySelector(this.selectors.crossSellSection);
      if (currentCrossSell && newCrossSell) {
        this.destroySwiper();
        currentCrossSell.innerHTML = newCrossSell.innerHTML;
        this.initSwiper();
      }

      // Bundle promo
      const newBundlePromo = newDrawer.querySelector(this.selectors.bundlePromo);
      const currentBundlePromo = this.drawer.querySelector(this.selectors.bundlePromo);
      if (currentBundlePromo && newBundlePromo) {
        currentBundlePromo.outerHTML = newBundlePromo.outerHTML;
      } else if (!currentBundlePromo && newBundlePromo) {
        const cta = this.drawer.querySelector(this.selectors.checkoutBtn);
        if (cta) cta.insertAdjacentHTML('beforebegin', newBundlePromo.outerHTML);
      } else if (currentBundlePromo && !newBundlePromo) {
        currentBundlePromo.remove();
      }

      // Checkout button
      const newCheckout = newDrawer.querySelector(this.selectors.checkoutBtn);
      if (this.checkoutBtn && newCheckout) {
        if (newCheckout.hasAttribute('aria-disabled')) {
          this.checkoutBtn.setAttribute('aria-disabled', 'true');
        } else {
          this.checkoutBtn.removeAttribute('aria-disabled');
        }
      }
    },

    // ========================================
    // GIFT SELECTION - Independent verification
    // Runs AFTER every cart operation as final safety net
    // ========================================

    async verifyGiftSelection() {
      const el = this.drawer.querySelector(this.selectors.giftSelection);
      if (!el) return;

      try {
        const resp = await fetch('/cart.js', {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        const cart = await resp.json();

        let nonGiftTotal = 0;
        let giftInCart = false;
        for (const item of cart.items) {
          if (item.properties && item.properties['_gift'] === 'true') {
            giftInCart = true;
          } else {
            nonGiftTotal += item.final_line_price;
          }
        }

        const giftThreshold = parseInt(this.drawer.dataset.giftThreshold, 10) || 0;
        const eligible = nonGiftTotal >= giftThreshold;

        this.lastKnownTotal = nonGiftTotal;

        // Reset decline if cart total changed
        if (this.giftDeclined && eligible && nonGiftTotal !== this.giftDeclinedTotal) {
          this.giftDeclined = false;
        }

        // Toggle visibility
        if (!this.giftDeclined) {
          el.style.display = '';
          el.classList.toggle('hidden', !eligible || giftInCart);
        }

      } catch (e) {
        // Silent fail - gift visibility stays as-is
      }
    },

    // ========================================
    // FALLBACK - Uses /cart.js when section rendering fails
    // ========================================

    async refreshCartFallback(skipGiftCheck = false) {
      try {
        const resp = await fetch('/cart.js', {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });
        const cart = await resp.json();

        const nonGiftTotal = cart.items.reduce((sum, item) => {
          if (item.properties && item.properties['_gift'] === 'true') return sum;
          return sum + item.final_line_price;
        }, 0);

        const giftThreshold = parseInt(this.drawer.dataset.giftThreshold, 10) || 0;
        const shippingThreshold = parseInt(this.drawer.dataset.shippingThreshold, 10) || 0;

        // Update cart count
        document.querySelectorAll(this.selectors.countEl).forEach(el => {
          el.textContent = cart.item_count;
          el.classList.toggle('hidden', cart.item_count === 0);
        });

        // Update empty/items visibility
        const emptyEl = this.drawer.querySelector(this.selectors.emptyContainer);
        const itemsEl = this.drawer.querySelector(this.selectors.itemsWrapper);
        if (emptyEl) emptyEl.classList.toggle('hidden', cart.item_count > 0);
        if (itemsEl) itemsEl.classList.toggle('hidden', cart.item_count === 0);

        // Update progress bar
        let progressPercent = 0;
        let progressMsg = '';

        if (nonGiftTotal >= shippingThreshold) {
          progressPercent = 100;
          progressMsg = this.drawer.dataset.shippingReachedMsg || 'Félicitations ! Livraison offerte.';
        } else if (nonGiftTotal >= giftThreshold) {
          const rangePercent = (nonGiftTotal - giftThreshold) * 50 / Math.max(1, shippingThreshold - giftThreshold);
          progressPercent = 50 + rangePercent;
          const remaining = (shippingThreshold - nonGiftTotal) / 100;
          const amountStr = remaining.toFixed(2).replace('.', ',') + ' \u20AC';
          const tmpl = this.drawer.dataset.giftReachedMsg || 'Plus que <strong>[amount]</strong> pour profiter de la livraison offerte !';
          progressMsg = tmpl.replace('[amount]', amountStr);
        } else {
          progressPercent = nonGiftTotal * 50 / Math.max(1, giftThreshold);
          const remaining = (giftThreshold - nonGiftTotal) / 100;
          const amountStr = remaining.toFixed(2).replace('.', ',') + ' \u20AC';
          const tmpl = this.drawer.dataset.progressMsg || 'Plus que <strong>[amount]</strong> pour obtenir un cadeau offert !';
          progressMsg = tmpl.replace('[amount]', amountStr);
        }

        const fillEl = this.drawer.querySelector(this.selectors.progressFill);
        if (fillEl) fillEl.style.width = progressPercent + '%';

        const msgEl = this.drawer.querySelector(this.selectors.progressMessage);
        if (msgEl) msgEl.innerHTML = progressMsg;

        const dotGift = this.drawer.querySelector(this.selectors.progressDotGift);
        if (dotGift) dotGift.classList.toggle('cart-drawer__progress-dot--active', nonGiftTotal >= giftThreshold);

        const dotShipping = this.drawer.querySelector(this.selectors.progressDotShipping);
        if (dotShipping) dotShipping.classList.toggle('cart-drawer__progress-dot--active', nonGiftTotal >= shippingThreshold);

        // Auto-remove gift if ineligible
        if (!skipGiftCheck) {
          const giftInCart = cart.items.some(item => item.properties && item.properties['_gift'] === 'true');
          if (giftInCart && nonGiftTotal < giftThreshold) {
            const giftItems = cart.items.filter(item => item.properties && item.properties['_gift'] === 'true');
            for (const gift of giftItems) {
              await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: gift.key, quantity: 0 })
              });
            }
            if (giftItems.length > 0) {
              await this.refreshCartFallback(true);
              return;
            }
          }
        }

        // CTA subtotal
        const ctaSubtotal = this.drawer.querySelector('[data-cart-subtotal-cta]');
        if (ctaSubtotal) {
          const formatted = (cart.total_price / 100).toFixed(2).replace('.', ',') + ' \u20AC';
          ctaSubtotal.textContent = formatted;
        }

        // Checkout button
        if (this.checkoutBtn) {
          if (cart.item_count === 0) {
            this.checkoutBtn.setAttribute('aria-disabled', 'true');
          } else {
            this.checkoutBtn.removeAttribute('aria-disabled');
          }
        }

        this.updateBundlePrices(cart);
        document.dispatchEvent(new CustomEvent('cart:drawer-refreshed'));
      } catch (fallbackError) {
        console.error('Cart fallback refresh error:', fallbackError);
      }
    },

    async removeGiftIfIneligible() {
      try {
        const giftThreshold = parseInt(this.drawer.dataset.giftThreshold, 10);
        if (!giftThreshold) return false;

        const response = await fetch('/cart.js');
        const cartData = await response.json();

        const nonGiftTotal = cartData.items.reduce((sum, item) => {
          if (item.properties && item.properties['_gift'] === 'true') return sum;
          return sum + item.final_line_price;
        }, 0);

        if (nonGiftTotal < giftThreshold) {
          const giftItems = cartData.items.filter(item =>
            item.properties && item.properties['_gift'] === 'true'
          );

          for (const gift of giftItems) {
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ id: gift.key, quantity: 0 })
            });
          }

          return giftItems.length > 0;
        }

        return false;
      } catch (error) {
        console.error('Gift removal check error:', error);
        return false;
      }
    },

    async updateBundlePrices(cartData) {
      try {
        let cart = cartData;
        if (!cart) {
          const resp = await fetch('/cart.js');
          cart = await resp.json();
        }
        const minQty = parseInt(this.drawer.dataset.bundleMinQty || '2', 10);
        const active = cart.item_count >= minQty;

        document.querySelectorAll('.product-card__price--bundle').forEach(el => {
          el.hidden = !active;
        });
        document.querySelectorAll('.product-card__price--regular').forEach(el => {
          el.hidden = active;
        });
      } catch (e) {
        // Silent fail
      }
    },

    updateHeaderCount(count) {
      const headerCounts = document.querySelectorAll('[data-cart-count-display]');
      headerCounts.forEach(el => {
        el.textContent = count;
        el.classList.toggle('hidden', count === 0);
      });
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => CartDrawer.init());
  } else {
    CartDrawer.init();
  }

  window.CartDrawer = CartDrawer;
})();
